interface SearchRecord {
  url: string;
  title: string;
  kind: string;
  section: string;
  method?: string;
  path?: string;
  body: string;
}

interface Scored {
  record: SearchRecord;
  score: number;
}

let index: SearchRecord[] | null = null;
let loading: Promise<SearchRecord[]> | null = null;

function loadIndex(): Promise<SearchRecord[]> {
  if (index) return Promise.resolve(index);
  loading ??= fetch('/search-index.json')
    .then((response) => (response.ok ? response.json() : []))
    .then((data: SearchRecord[]) => {
      index = data;
      return data;
    })
    .catch(() => []);
  return loading;
}

function score(record: SearchRecord, query: string, terms: string[]): number {
  const title = record.title.toLowerCase();
  const path = (record.path ?? '').toLowerCase();
  const body = record.body.toLowerCase();

  let total = 0;
  if (title === query) total += 120;
  if (title.startsWith(query)) total += 60;
  if (title.includes(query)) total += 40;
  if (path.includes(query)) total += 35;

  for (const term of terms) {
    if (title.includes(term)) total += 14;
    if (path.includes(term)) total += 10;
    if (body.includes(term)) total += 4;
    if (record.section.toLowerCase().includes(term)) total += 3;
  }

  // Endpoint pages are usually what people are hunting for.
  if (record.kind === 'operation') total += 6;
  return total;
}

function render(container: HTMLElement, results: Scored[], query: string): void {
  if (!query) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  container.hidden = false;

  if (!results.length) {
    container.innerHTML = `<p class="search-empty">No matches for “${escapeHtml(query)}”.</p>`;
    return;
  }

  container.innerHTML = results
    .map(({ record }, i) => {
      const meta = record.method
        ? `${record.method} ${record.path ?? ''}`
        : record.section;
      return `<a class="search-result${i === 0 ? ' is-active' : ''}" href="${record.url}">
        <strong>${escapeHtml(record.title)}</strong>
        <span>${escapeHtml(meta)}</span>
      </a>`;
    })
    .join('');
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

export function initSearch(): void {
  const form = document.querySelector<HTMLFormElement>('[data-search]');
  const input = document.querySelector<HTMLInputElement>('[data-search-input]');
  const results = document.querySelector<HTMLElement>('[data-search-results]');
  if (!form || !input || !results) return;

  let current: Scored[] = [];
  let activeIndex = 0;

  const run = async () => {
    const query = input.value.trim().toLowerCase();
    if (query.length < 2) {
      current = [];
      render(results, current, '');
      return;
    }

    const records = await loadIndex();
    const terms = query.split(/\s+/).filter(Boolean);
    current = records
      .map((record) => ({ record, score: score(record, query, terms) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
    activeIndex = 0;
    render(results, current, query);
  };

  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void run(), 110);
  });

  input.addEventListener('focus', () => void loadIndex());

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const target = current[activeIndex]?.record.url;
    if (target) window.location.href = target;
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      input.value = '';
      render(results, [], '');
      input.blur();
      return;
    }

    const delta = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (!delta || !current.length) return;

    event.preventDefault();
    activeIndex = (activeIndex + delta + current.length) % current.length;
    const items = results.querySelectorAll('.search-result');
    items.forEach((item, i) => item.classList.toggle('is-active', i === activeIndex));
    items[activeIndex]?.scrollIntoView({ block: 'nearest' });
  });

  document.addEventListener('click', (event) => {
    if (!form.contains(event.target as Node) && !results.contains(event.target as Node)) {
      render(results, [], '');
    }
  });

  // "/" focuses search, the way most docs sites behave.
  document.addEventListener('keydown', (event) => {
    const tag = (event.target as HTMLElement | null)?.tagName;
    if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
      event.preventDefault();
      input.focus();
      input.select();
    }
  });

  // Deep link support: /?q=streams pre-fills and runs the search.
  const initial = new URLSearchParams(window.location.search).get('q');
  if (initial) {
    input.value = initial;
    void run();
  }
}
